import { useApi } from "@/api";
import {
  ContentType,
  TAccount,
  TAccountType,
  TCurrency,
  TTransaction,
} from "@/api/v1_0";
import * as Domain from "@/domain";
import { TrackEventEnum } from "@/store/logging/types";
import { RootStore } from "@/store/root";
import { TAccountsMap, TRatesMap, TTransactionsMap } from "@/types";
import { makeAutoObservable } from "mobx";
import {
  EXPENSE_CATEGORIES_LIMIT,
  INCOME_SOURCES_LIMIT,
  SAVINGS_LIMIT,
} from "./consts";

export class WalletStore {
  private _rootStore: RootStore;

  public loading = true;
  public accountLoading = false;
  public ratesLoading = false;
  public submitting = false;

  public accounts: TAccountsMap = new Map<TAccount["uuid"], TAccount>();
  public accountTransactions: Map<TAccount["uuid"], TTransactionsMap> = new Map<
    TAccount["uuid"],
    TTransactionsMap
  >();
  public rates: TRatesMap = new Map<
    TCurrency.EUR | TCurrency.RUB | TCurrency.USD,
    number
  >();
  public transactions: TTransactionsMap = new Map<
    TTransaction["uuid"],
    TTransaction
  >();

  constructor(rootStore: RootStore) {
    makeAutoObservable(this);

    this._rootStore = rootStore;
  }

  public get debts(): TAccountsMap {
    return [...this.accounts.values()]
      .filter((account) => TAccountType.Debt === account.type)
      .reduce(
        (result, account) => result.set(account.uuid, account),
        new Map<TAccount["uuid"], TAccount>()
      );
  }

  public get expenseCategories(): TAccountsMap {
    return [...this.accounts.values()]
      .filter((account) => TAccountType.ExpenseCategory === account.type)
      .reduce(
        (result, account) => result.set(account.uuid, account),
        new Map<TAccount["uuid"], TAccount>()
      );
  }

  public get goals(): TAccountsMap {
    return [...this.accounts.values()]
      .filter((account) => TAccountType.Goal === account.type)
      .reduce(
        (result, account) => result.set(account.uuid, account),
        new Map<TAccount["uuid"], TAccount>()
      );
  }

  public get incomeSources(): TAccountsMap {
    return [...this.accounts.values()]
      .filter((account) => TAccountType.IncomeSource === account.type)
      .reduce(
        (result, account) => result.set(account.uuid, account),
        new Map<TAccount["uuid"], TAccount>()
      );
  }

  public get savings(): TAccountsMap {
    return [...this.accounts.values()]
      .filter((account) => TAccountType.Saving === account.type)
      .reduce(
        (result, account) => result.set(account.uuid, account),
        new Map<TAccount["uuid"], TAccount>()
      );
  }

  public load = async (): Promise<void> => {
    this._rootStore.logging.track("wallet.load");

    try {
      this.loading = true;

      const api = useApi();

      const { data: result } =
        await api.v10.appHttpApiV10ControllerWalletAccountsRead(
          {
            page: 1,
            limit: 1024,
          },
          {
            headers: {
              "Content-Type": ContentType.Json,
            },
          }
        );

      this.accounts = result.items.reduce(
        (accounts: TAccountsMap, account: TAccount) =>
          accounts.set(account.uuid, account),
        new Map<TAccount["uuid"], TAccount>()
      );
    } catch (err) {
      throw this._rootStore.logging.capture(err);
    } finally {
      this.loading = false;
    }
  };

  public loadAccount = async (uuid: string): Promise<void> => {
    this._rootStore.logging.track("wallet.loadAccount", { uuid });

    try {
      const api = useApi();

      await Promise.all([
        api.v10.appHttpApiV10ControllerWalletAccountRead(uuid, {
          headers: {
            "Content-Type": ContentType.Json,
          },
        }),
        api.v10.appHttpApiV10ControllerWalletAccountTransactionsRead(
          {
            account: uuid,
            page: 1,
            limit: 1024,
          },
          {
            headers: {
              "Content-Type": ContentType.Json,
            },
          }
        ),
      ]).then((result) => {
        const account = result[0].data;
        const accountTransactions = result[1].data;

        this.accounts.set(account.uuid, account);

        this.accountTransactions.set(
          account.uuid,
          accountTransactions.items.reduce(
            (transactions: TTransactionsMap, transaction: TTransaction) =>
              transactions.set(transaction.uuid, transaction),
            new Map<TTransaction["uuid"], TTransaction>()
          )
        );
      });
    } catch (err) {
      throw this._rootStore.logging.capture(err);
    } finally {
      this.accountLoading = false;
    }
  };

  public loadRates = async (): Promise<void> => {
    this._rootStore.logging.track("wallet.loadRates");

    try {
      this.ratesLoading = true;

      const api = useApi();

      const { data: result } =
        await api.v10.appHttpApiV10ControllerExchangeRatesRead(
          {
            currency: this._rootStore.app.currency,
            precision: 2,
            date: undefined,
          },
          {
            headers: {
              "Content-Type": ContentType.Json,
            },
          }
        );

      this.rates = Object.keys(result).reduce(
        (rates, currency: any) =>
          rates.set(currency, result[currency] as unknown as number),
        new Map<TCurrency.EUR | TCurrency.RUB | TCurrency.USD, number>()
      );
    } catch (err) {
      throw this._rootStore.logging.capture(err);
    } finally {
      this.ratesLoading = false;
    }
  };

  public showAddDebt = (): void => {
    // navigate<IDebtEditNavigationParams>("Debt.Edit");
  };

  public saveDebt = async (values: Domain.Debt.IInput): Promise<void> => {
    this._rootStore.logging.track("wallet.saveDebt", { values });

    const v = Domain.Debt.validate(values);
    if (v.failed) {
      throw v.errors;
    }

    try {
      this.submitting = true;

      const api = useApi();

      const { data: result } = values.uuid
        ? await api.v10.appHttpApiV10ControllerWalletAccountUpdate(
            v.values.uuid,
            v.values,
            {
              headers: {
                "Content-Type": ContentType.Json,
              },
            }
          )
        : await api.v10.appHttpApiV10ControllerWalletAccountsCreate(v.values, {
            headers: {
              "Content-Type": ContentType.Json,
            },
          });

      this.accounts.set(result.uuid, result);

      this._rootStore.logging.track(
        values.uuid ? TrackEventEnum.DEBT_UPDATED : TrackEventEnum.DEBT_ADDED
      );
    } catch (err) {
      throw this._rootStore.logging.capture(err);
    } finally {
      this.submitting = false;
    }
  };

  public removeDebt = async (debtUuid: string): Promise<void> => {
    this._rootStore.logging.track("wallet.removeDebt", { uuid: debtUuid });

    try {
      this.submitting = true;

      const api = useApi();

      await api.v10.appHttpApiV10ControllerWalletAccountDelete(
        debtUuid,
        {},
        {
          headers: {
            "Content-Type": ContentType.Json,
          },
        }
      );

      this.accounts.delete(debtUuid);

      this._rootStore.logging.track(TrackEventEnum.DEBT_REMOVED);
    } catch (err) {
      throw this._rootStore.logging.capture(err);
    } finally {
      this.submitting = false;
    }
  };

  public showAddExpenseCategory = (): void => {
    if (
      !this._rootStore.profile.pro &&
      EXPENSE_CATEGORIES_LIMIT <= this.expenseCategories.size
    ) {
      this._rootStore.popup.expenseCategoriesLimit = true;

      return;
    }

    // navigate<IExpenseCategoryEditNavigationParams>("ExpenseCategory.Edit");
  };

  public saveExpenseCategory = async (
    values: Domain.ExpenseCategory.IInput
  ): Promise<void> => {
    this._rootStore.logging.track("wallet.saveExpsenseCategory", { values });

    const v = Domain.ExpenseCategory.validate(values);
    if (v.failed) {
      throw v.errors;
    }

    try {
      this.submitting = true;

      const api = useApi();

      const { data: result } = values.uuid
        ? await api.v10.appHttpApiV10ControllerWalletAccountUpdate(
            v.values.uuid,
            v.values,
            {
              headers: {
                "Content-Type": ContentType.Json,
              },
            }
          )
        : await api.v10.appHttpApiV10ControllerWalletAccountsCreate(v.values, {
            headers: {
              "Content-Type": ContentType.Json,
            },
          });

      this.accounts.set(result.uuid, result);

      this._rootStore.logging.track(
        values.uuid
          ? TrackEventEnum.EXPENSE_CATEGORY_UPDATED
          : TrackEventEnum.EXPENSE_CATEGORY_ADDED
      );
    } catch (err) {
      throw this._rootStore.logging.capture(err);
    } finally {
      this.submitting = false;
    }
  };

  public removeExpenseCategory = async (
    expenseCategoryUuid: string
  ): Promise<void> => {
    this._rootStore.logging.track("wallet.removeExpenseCategory", {
      uuid: expenseCategoryUuid,
    });

    try {
      this.submitting = true;

      const api = useApi();

      await api.v10.appHttpApiV10ControllerWalletAccountDelete(
        expenseCategoryUuid,
        {},
        {
          headers: {
            "Content-Type": ContentType.Json,
          },
        }
      );

      this.accounts.delete(expenseCategoryUuid);

      this._rootStore.logging.track(TrackEventEnum.EXPENSE_CATEGORY_REMOVED);
    } catch (err) {
      throw this._rootStore.logging.capture(err);
    } finally {
      this.submitting = false;
    }
  };

  public showAddGoal = (): void => {
    // navigate<IGoalEditNavigationParams>("Goal.Edit");
  };

  public saveGoal = async (values: Domain.Goal.IInput): Promise<void> => {
    this._rootStore.logging.track("wallet.saveGoal", { values });

    const v = Domain.Goal.validate(values);
    if (v.failed) {
      throw v.errors;
    }

    try {
      this.submitting = true;

      const api = useApi();

      const { data: result } = values.uuid
        ? await api.v10.appHttpApiV10ControllerWalletAccountUpdate(
            v.values.uuid,
            v.values,
            {
              headers: {
                "Content-Type": ContentType.Json,
              },
            }
          )
        : await api.v10.appHttpApiV10ControllerWalletAccountsCreate(v.values, {
            headers: {
              "Content-Type": ContentType.Json,
            },
          });

      this.accounts.set(result.uuid, result);

      this._rootStore.logging.track(
        values.uuid ? TrackEventEnum.GOAL_UPDATED : TrackEventEnum.GOAL_ADDED
      );
    } catch (err) {
      throw this._rootStore.logging.capture(err);
    } finally {
      this.submitting = false;
    }
  };

  public removeGoal = async (goalUuid: string): Promise<void> => {
    this._rootStore.logging.track("wallet.removeGoal", { uuid: goalUuid });

    try {
      this.submitting = true;

      const api = useApi();

      await api.v10.appHttpApiV10ControllerWalletAccountDelete(
        goalUuid,
        {},
        {
          headers: {
            "Content-Type": ContentType.Json,
          },
        }
      );

      this.accounts.delete(goalUuid);

      this._rootStore.logging.track(TrackEventEnum.GOAL_REMOVED);
    } catch (err) {
      throw this._rootStore.logging.capture(err);
    } finally {
      this.submitting = false;
    }
  };

  public showAddIncomeSource = (): void => {
    if (
      !this._rootStore.profile.pro &&
      INCOME_SOURCES_LIMIT <= this.incomeSources.size
    ) {
      this._rootStore.popup.incomeSourcesLimit = true;

      return;
    }

    // navigate<IIncomeSourceEditNavigationParams>("IncomeSource.Edit");
  };

  public saveIncomeSource = async (
    values: Domain.IncomeSource.IInput
  ): Promise<void> => {
    this._rootStore.logging.track("wallet.saveIncomeSource", { values });

    const v = Domain.IncomeSource.validate(values);
    if (v.failed) {
      throw v.errors;
    }

    try {
      this.submitting = true;

      const api = useApi();

      const { data: result } = values.uuid
        ? await api.v10.appHttpApiV10ControllerWalletAccountUpdate(
            v.values.uuid,
            v.values,
            {
              headers: {
                "Content-Type": ContentType.Json,
              },
            }
          )
        : await api.v10.appHttpApiV10ControllerWalletAccountsCreate(v.values, {
            headers: {
              "Content-Type": ContentType.Json,
            },
          });

      this.accounts.set(result.uuid, result);

      this._rootStore.logging.track(
        values.uuid
          ? TrackEventEnum.INCOME_SOURCE_UPDATED
          : TrackEventEnum.INCOME_SOURCE_ADDED
      );
    } catch (err) {
      throw this._rootStore.logging.capture(err);
    } finally {
      this.submitting = false;
    }
  };

  public removeIncomeSource = async (
    incomeSourceUuid: string
  ): Promise<void> => {
    this._rootStore.logging.track("wallet.removeIncomeSource", {
      uuid: incomeSourceUuid,
    });

    try {
      this.submitting = true;

      const api = useApi();

      await api.v10.appHttpApiV10ControllerWalletAccountDelete(
        incomeSourceUuid,
        {},
        {
          headers: {
            "Content-Type": ContentType.Json,
          },
        }
      );

      this.accounts.delete(incomeSourceUuid);

      this._rootStore.logging.track(TrackEventEnum.INCOME_SOURCE_REMOVED);
    } catch (err) {
      throw this._rootStore.logging.capture(err);
    } finally {
      this.submitting = false;
    }
  };

  public showAddSaving = (): void => {
    if (!this._rootStore.profile.pro && SAVINGS_LIMIT <= this.savings.size) {
      this._rootStore.popup.savingsLimit = true;

      return;
    }

    // navigate<ISavingEditNavigationParams>("Saving.Edit");
  };

  public saveSaving = async (values: Domain.Saving.IInput): Promise<void> => {
    this._rootStore.logging.track("wallet.saveSaving", { values });

    const v = Domain.Saving.validate(values, this._rootStore.app.country);
    if (v.failed) {
      throw v.errors;
    }

    try {
      this.submitting = true;

      const api = useApi();

      const { data: result } = values.uuid
        ? await api.v10.appHttpApiV10ControllerWalletAccountUpdate(
            v.values.uuid,
            v.values,
            {
              headers: {
                "Content-Type": ContentType.Json,
              },
            }
          )
        : await api.v10.appHttpApiV10ControllerWalletAccountsCreate(v.values, {
            headers: {
              "Content-Type": ContentType.Json,
            },
          });

      this.accounts.set(result.uuid, result);

      this._rootStore.logging.track(
        values.uuid
          ? TrackEventEnum.SAVING_UPDATED
          : TrackEventEnum.SAVING_ADDED
      );
    } catch (err) {
      throw this._rootStore.logging.capture(err);
    } finally {
      this.submitting = false;
    }
  };

  public removeSaving = async (savingUuid: string): Promise<void> => {
    this._rootStore.logging.track("wallet.removeSaving", { uuid: savingUuid });

    try {
      this.submitting = true;

      const api = useApi();

      await api.v10.appHttpApiV10ControllerWalletAccountDelete(
        savingUuid,
        {},
        {
          headers: {
            "Content-Type": ContentType.Json,
          },
        }
      );

      this.accounts.delete(savingUuid);

      this._rootStore.logging.track(TrackEventEnum.SAVING_REMOVED);
    } catch (err) {
      throw this._rootStore.logging.capture(err);
    } finally {
      this.submitting = false;
    }
  };

  public saveTransaction = async (
    values: Domain.Transaction.IInput
  ): Promise<void> => {
    this._rootStore.logging.track("wallet.saveTransaction", { values });

    const v = Domain.Transaction.validate(values);
    if (v.failed) {
      throw v.errors;
    }

    try {
      this.submitting = true;

      const api = useApi();

      const { data: result } = values.uuid
        ? await api.v10.appHttpApiV10ControllerWalletTransactionUpdate(
            v.values.uuid,
            v.values,
            {
              headers: {
                "Content-Type": ContentType.Json,
              },
            }
          )
        : await api.v10.appHttpApiV10ControllerWalletTransactionsCreate(
            v.values,
            {
              headers: {
                "Content-Type": ContentType.Json,
              },
            }
          );

      this.accounts.set(result.accounts[0].uuid, result.accounts[0]);
      this.accounts.set(result.accounts[1].uuid, result.accounts[1]);
      this.transactions.set(result.transaction.uuid, result.transaction);

      if (!this.accountTransactions.has(result.accounts[0].uuid)) {
        this.accountTransactions.set(
          result.accounts[0].uuid,
          new Map<TTransaction["uuid"], TTransaction>()
        );
      }

      this.accountTransactions
        .get(result.accounts[0].uuid)!
        .set(result.transaction.uuid, result.transaction);

      if (!this.accountTransactions.has(result.accounts[1].uuid)) {
        this.accountTransactions.set(
          result.accounts[1].uuid,
          new Map<TTransaction["uuid"], TTransaction>()
        );
      }

      this.accountTransactions
        .get(result.accounts[1].uuid)!
        .set(result.transaction.uuid, result.transaction);

      if (this._rootStore.history.transactions.has(result.transaction.uuid)) {
        this._rootStore.history.transactions.set(
          result.transaction.uuid,
          result.transaction
        );
      }

      //	No await here, it's not blocking operation.
      this._rootStore.dashboard.load();
      this._rootStore.operations.load();

      this._rootStore.logging.track(
        values.uuid
          ? TrackEventEnum.TRANSACTION_UPDATED
          : TrackEventEnum.TRANSACTION_ADDED
      );
    } catch (err) {
      throw this._rootStore.logging.capture(err);
    } finally {
      this.submitting = false;
    }
  };

  public removeTransaction = async (transactionUuid: string): Promise<void> => {
    this._rootStore.logging.track("wallet.removeTransaction", {
      uuid: transactionUuid,
    });

    try {
      this.submitting = true;

      const api = useApi();

      const { data: result } =
        await api.v10.appHttpApiV10ControllerWalletTransactionDelete(
          transactionUuid,
          {},
          {
            headers: {
              "Content-Type": ContentType.Json,
            },
          }
        );

      this.accounts.set(result.accounts[0].uuid, result.accounts[0]);
      this.accounts.set(result.accounts[1].uuid, result.accounts[1]);
      this.transactions.delete(transactionUuid);
      this._rootStore.history.transactions.delete(transactionUuid);

      if (this.accountTransactions.has(result.accounts[0].uuid)) {
        this.accountTransactions.get(result.accounts[0].uuid)!.clear();
      }

      if (this.accountTransactions.has(result.accounts[1].uuid)) {
        this.accountTransactions.get(result.accounts[1].uuid)!.clear();
      }

      if (this._rootStore.history.transactions.has(transactionUuid)) {
        this._rootStore.history.transactions.delete(transactionUuid);
      }

      //	No await here, it's not blocking operation.
      this._rootStore.dashboard.load();
      this._rootStore.operations.load();

      this._rootStore.logging.track(TrackEventEnum.TRANSACTION_REMOVED);
    } catch (err) {
      throw this._rootStore.logging.capture(err);
    } finally {
      this.submitting = false;
    }
  };
}
